import { faker } from '@faker-js/faker'
import type { Product, Badge } from '../../types/product'
import { CATEGORIES } from '../seeds/categories'
import { createVendor } from './vendor'

faker.seed(12345)

const DISCOUNT_OPTIONS = [0, 10, 15, 20, 25, 30, 40, 50]
const BADGE_OPTIONS: Badge[] = ['choice', 'top_seller', 'new', 'verified']

// Grocery-specific product names by category
const GROCERY_PRODUCTS: Record<string, { products: string[]; units: string[]; weights: string[] }> = {
  'fresh-produce': {
    products: [
      'Red Apples', 'Green Apples', 'Bananas', 'Oranges', 'Grapes', 'Strawberries', 'Blueberries',
      'Tomatoes', 'Potatoes', 'Onions', 'Carrots', 'Broccoli', 'Spinach', 'Lettuce', 'Cucumber',
      'Bell Peppers', 'Cauliflower', 'Cabbage', 'Celery', 'Garlic', 'Ginger', 'Lemon', 'Lime',
      'Mango', 'Papaya', 'Watermelon', 'Pineapple', 'Avocado', 'Kiwi', 'Pomegranate',
      'Fresh Coriander', 'Fresh Mint', 'Fresh Basil', 'Green Chili', 'Mushrooms'
    ],
    units: ['kg', 'g', 'bunch', 'piece'],
    weights: ['500g', '1kg', '2kg', '250g', '1 bunch', '6 pieces']
  },
  'dairy-eggs': {
    products: [
      'Full Cream Milk', 'Low Fat Milk', 'Skimmed Milk', 'Chocolate Milk', 'Almond Milk',
      'Cheddar Cheese', 'Mozzarella Cheese', 'Cream Cheese', 'Cottage Cheese', 'Parmesan',
      'Farm Fresh Eggs', 'Organic Eggs', 'Free Range Eggs', 'Duck Eggs',
      'Greek Yogurt', 'Plain Yogurt', 'Flavored Yogurt', 'Probiotic Yogurt',
      'Salted Butter', 'Unsalted Butter', 'Ghee', 'Fresh Cream', 'Sour Cream',
      'Condensed Milk', 'Evaporated Milk', 'Buttermilk'
    ],
    units: ['liter', 'ml', 'pack', 'dozen', 'g'],
    weights: ['500ml', '1 liter', '250g', '6 pack', '12 pack', '200g']
  },
  'bakery': {
    products: [
      'White Bread', 'Whole Wheat Bread', 'Multigrain Bread', 'Sourdough Bread', 'Rye Bread',
      'Croissants', 'Danish Pastry', 'Cinnamon Rolls', 'Donuts', 'Muffins',
      'Chocolate Cake', 'Vanilla Cake', 'Red Velvet Cake', 'Cheesecake', 'Fruit Cake',
      'Burger Buns', 'Hot Dog Buns', 'Dinner Rolls', 'Bagels', 'Pita Bread',
      'Cookies', 'Brownies', 'Cupcakes', 'Macarons', 'Eclairs'
    ],
    units: ['pack', 'piece', 'loaf', 'box'],
    weights: ['1 loaf', '4 pack', '6 pack', '500g', '250g']
  },
  'meat-seafood': {
    products: [
      'Chicken Breast', 'Chicken Thighs', 'Whole Chicken', 'Chicken Wings', 'Chicken Drumsticks',
      'Beef Steak', 'Ground Beef', 'Beef Ribs', 'Beef Tenderloin', 'Beef Brisket',
      'Lamb Chops', 'Ground Lamb', 'Lamb Leg', 'Mutton Curry Cut',
      'Salmon Fillet', 'Tuna Steak', 'Cod Fillet', 'Prawns', 'Shrimp', 'Crab',
      'Tilapia', 'Hilsa Fish', 'Rohu Fish', 'Catfish', 'Mackerel'
    ],
    units: ['kg', 'g', 'piece', 'pack'],
    weights: ['500g', '1kg', '250g', '300g', '750g']
  },
  'pantry-staples': {
    products: [
      'Basmati Rice', 'Jasmine Rice', 'Brown Rice', 'White Rice', 'Sticky Rice',
      'All Purpose Flour', 'Whole Wheat Flour', 'Bread Flour', 'Corn Flour', 'Rice Flour',
      'Vegetable Oil', 'Olive Oil', 'Coconut Oil', 'Sunflower Oil', 'Mustard Oil',
      'Turmeric Powder', 'Cumin Powder', 'Coriander Powder', 'Chili Powder', 'Garam Masala',
      'White Sugar', 'Brown Sugar', 'Salt', 'Sea Salt', 'Black Pepper',
      'Canned Tomatoes', 'Tomato Paste', 'Pasta', 'Spaghetti', 'Noodles',
      'Lentils', 'Chickpeas', 'Kidney Beans', 'Black Beans', 'Dried Peas'
    ],
    units: ['kg', 'g', 'liter', 'ml', 'pack'],
    weights: ['1kg', '5kg', '500g', '1 liter', '500ml', '250g']
  },
  'frozen-foods': {
    products: [
      'Frozen Peas', 'Frozen Corn', 'Frozen Mixed Vegetables', 'Frozen Spinach', 'Frozen Broccoli',
      'Vanilla Ice Cream', 'Chocolate Ice Cream', 'Strawberry Ice Cream', 'Mango Ice Cream',
      'Frozen Pizza', 'Frozen Samosas', 'Frozen Parathas', 'Frozen Spring Rolls',
      'Fish Fingers', 'Chicken Nuggets', 'Frozen Burgers', 'Frozen Fries',
      'Frozen Berries', 'Frozen Mango', 'Ice Cream Cake', 'Frozen Dumplings'
    ],
    units: ['pack', 'kg', 'g', 'box'],
    weights: ['500g', '1kg', '250g', '1 liter', '2 liter']
  },
  'beverages': {
    products: [
      'Mineral Water', 'Spring Water', 'Sparkling Water', 'Coconut Water',
      'Orange Juice', 'Apple Juice', 'Mango Juice', 'Mixed Fruit Juice', 'Pomegranate Juice',
      'Cola', 'Lemon Soda', 'Ginger Ale', 'Tonic Water', 'Energy Drink',
      'Green Tea', 'Black Tea', 'Herbal Tea', 'Iced Tea', 'Chamomile Tea',
      'Instant Coffee', 'Ground Coffee', 'Coffee Beans', 'Espresso Pods', 'Decaf Coffee'
    ],
    units: ['liter', 'ml', 'pack', 'bottle', 'can'],
    weights: ['500ml', '1 liter', '2 liter', '250ml', '6 pack', '12 pack']
  },
  'snacks': {
    products: [
      'Potato Chips', 'Tortilla Chips', 'Pita Chips', 'Veggie Chips', 'Banana Chips',
      'Digestive Biscuits', 'Cream Biscuits', 'Chocolate Cookies', 'Butter Cookies', 'Crackers',
      'Milk Chocolate', 'Dark Chocolate', 'White Chocolate', 'Chocolate Truffles', 'Chocolate Bar',
      'Roasted Almonds', 'Cashews', 'Peanuts', 'Mixed Nuts', 'Pistachios',
      'Gummy Bears', 'Hard Candy', 'Lollipops', 'Caramel', 'Toffee',
      'Popcorn', 'Pretzels', 'Trail Mix', 'Granola Bars', 'Energy Bars'
    ],
    units: ['pack', 'g', 'box', 'bag'],
    weights: ['100g', '200g', '500g', '250g', '150g']
  },
  'household': {
    products: [
      'Dish Soap', 'Dishwasher Detergent', 'Surface Cleaner', 'Glass Cleaner', 'Floor Cleaner',
      'Toilet Paper', 'Paper Towels', 'Facial Tissues', 'Napkins', 'Kitchen Roll',
      'Laundry Detergent', 'Fabric Softener', 'Stain Remover', 'Bleach', 'Washing Powder',
      'Sponges', 'Scrub Brush', 'Mop Refill', 'Trash Bags', 'Storage Bags',
      'Aluminum Foil', 'Plastic Wrap', 'Parchment Paper', 'Food Containers', 'Zip Lock Bags'
    ],
    units: ['pack', 'liter', 'ml', 'piece', 'roll'],
    weights: ['1 liter', '500ml', '12 pack', '6 pack', '100 count']
  },
  'personal-care': {
    products: [
      'Bar Soap', 'Liquid Hand Soap', 'Body Wash', 'Shower Gel', 'Antibacterial Soap',
      'Shampoo', 'Conditioner', 'Hair Oil', 'Hair Gel', 'Hair Spray',
      'Toothpaste', 'Toothbrush', 'Mouthwash', 'Dental Floss', 'Whitening Strips',
      'Face Wash', 'Moisturizer', 'Sunscreen', 'Face Cream', 'Body Lotion',
      'Deodorant', 'Antiperspirant', 'Body Spray', 'Razors', 'Shaving Cream'
    ],
    units: ['ml', 'g', 'pack', 'piece'],
    weights: ['200ml', '500ml', '100g', '75g', '4 pack']
  }
}

// High-quality Unsplash image URLs for grocery items by category
const PRODUCT_IMAGES: Record<string, string[]> = {
  'fresh-produce': [
    'https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=500&h=500&fit=crop&q=80', // Fresh vegetables
    'https://images.unsplash.com/photo-1542838132-92c53300491e?w=500&h=500&fit=crop&q=80', // Fruits basket
    'https://images.unsplash.com/photo-1566385101042-1a0aa0c1268c?w=500&h=500&fit=crop&q=80', // Tomatoes
    'https://images.unsplash.com/photo-1590779033100-9f60a05a013d?w=500&h=500&fit=crop&q=80', // Apples
    'https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=500&h=500&fit=crop&q=80', // Bananas
    'https://images.unsplash.com/photo-1560806887-1e4cd0b6cbd6?w=500&h=500&fit=crop&q=80', // Carrots
    'https://images.unsplash.com/photo-1552566626-52f8b828add9?w=500&h=500&fit=crop&q=80', // Cucumbers
    'https://images.unsplash.com/photo-1514984879728-be0aff75a6e8?w=500&h=500&fit=crop&q=80', // Bell peppers
    'https://images.unsplash.com/photo-1550583724-b2692b85b150?w=500&h=500&fit=crop&q=80', // Leafy greens
    'https://images.unsplash.com/photo-1598170845058-32b9d6a5da37?w=500&h=500&fit=crop&q=80', // Onions
  ],
  'dairy-eggs': [
    'https://images.unsplash.com/photo-1563636619-e9143da7973b?w=500&h=500&fit=crop&q=80', // Milk bottles
    'https://images.unsplash.com/photo-1452195100486-9cc805987862?w=500&h=500&fit=crop&q=80', // Cheese board
    'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=500&h=500&fit=crop&q=80', // Fresh eggs
    'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=500&h=500&fit=crop&q=80', // Yogurt
    'https://images.unsplash.com/photo-1628088062854-d1870b4553da?w=500&h=500&fit=crop&q=80', // Butter
    'https://images.unsplash.com/photo-1618164436241-4473940d1f5c?w=500&h=500&fit=crop&q=80', // Cheese slices
    'https://images.unsplash.com/photo-1589881133595-7c9e6e8e9d1d?w=500&h=500&fit=crop&q=80', // Milk glass
    'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=500&h=500&fit=crop&q=80', // Cream
  ],
  'bakery': [
    'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=500&h=500&fit=crop&q=80', // Fresh bread
    'https://images.unsplash.com/photo-1555507036-ab1f4038808a?w=500&h=500&fit=crop&q=80', // Croissants
    'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=500&h=500&fit=crop&q=80', // Bakery items
    'https://images.unsplash.com/photo-1486427944299-d1955d23e34d?w=500&h=500&fit=crop&q=80', // Chocolate cake
    'https://images.unsplash.com/photo-1557925923-cd4648e211a0?w=500&h=500&fit=crop&q=80', // Muffins
    'https://images.unsplash.com/photo-1608198399988-841b704f9c9e?w=500&h=500&fit=crop&q=80', // Cookies
    'https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=500&h=500&fit=crop&q=80', // Donuts
    'https://images.unsplash.com/photo-1612182062231-886fd6eb74d7?w=500&h=500&fit=crop&q=80', // Buns
  ],
  'meat-seafood': [
    'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=500&h=500&fit=crop&q=80', // Raw chicken
    'https://images.unsplash.com/photo-1603048297172-c92544798d5a?w=500&h=500&fit=crop&q=80', // Beef steak
    'https://images.unsplash.com/photo-1580476262798-bddd9f4b7369?w=500&h=500&fit=crop&q=80', // Salmon fillet
    'https://images.unsplash.com/photo-1565680018434-b513d5e5fd47?w=500&h=500&fit=crop&q=80', // Fresh fish
    'https://images.unsplash.com/photo-1633237308525-cd587cf71926?w=500&h=500&fit=crop&q=80', // Prawns
    'https://images.unsplash.com/photo-1615485736704-5c826ab70334?w=500&h=500&fit=crop&q=80', // Lamb chops
    'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=500&h=500&fit=crop&q=80', // Chicken cuts
    'https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?w=500&h=500&fit=crop&q=80', // Seafood
  ],
  'pantry-staples': [
    'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=500&h=500&fit=crop&q=80', // Rice
    'https://images.unsplash.com/photo-1556909212-d5b604d0c90d?w=500&h=500&fit=crop&q=80', // Flour & spices
    'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=500&h=500&fit=crop&q=80', // Cooking oil
    'https://images.unsplash.com/photo-1596040033229-a0b63564a6bf?w=500&h=500&fit=crop&q=80', // Spices
    'https://images.unsplash.com/photo-1551462147-ff29053bfc14?w=500&h=500&fit=crop&q=80', // Pasta
    'https://images.unsplash.com/photo-1539252554453-80ab65ce3586?w=500&h=500&fit=crop&q=80', // Lentils
    'https://images.unsplash.com/photo-1516714819001-8ee7a13b71d7?w=500&h=500&fit=crop&q=80', // Canned goods
    'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=500&h=500&fit=crop&q=80', // Beans
  ],
  'frozen-foods': [
    'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=500&h=500&fit=crop&q=80', // Ice cream
    'https://images.unsplash.com/photo-1488900128323-21503983a07e?w=500&h=500&fit=crop&q=80', // Frozen vegetables
    'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=500&h=500&fit=crop&q=80', // Pizza
    'https://images.unsplash.com/photo-1501443762994-82bd5dace89a?w=500&h=500&fit=crop&q=80', // Frozen berries
    'https://images.unsplash.com/photo-1548048497-1f6b28b73f96?w=500&h=500&fit=crop&q=80', // Ice cream cone
    'https://images.unsplash.com/photo-1628191010210-a59de33e6162?w=500&h=500&fit=crop&q=80', // Frozen meals
    'https://images.unsplash.com/photo-1561395879-2d1b2a22b193?w=500&h=500&fit=crop&q=80', // Popsicles
  ],
  'beverages': [
    'https://images.unsplash.com/photo-1523677011781-c91d1bbe2f9d?w=500&h=500&fit=crop&q=80', // Water bottle
    'https://images.unsplash.com/photo-1600271886742-f049cd451bba?w=500&h=500&fit=crop&q=80', // Orange juice
    'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=500&h=500&fit=crop&q=80', // Coffee beans
    'https://images.unsplash.com/photo-1564890369478-c89ca6d9cde9?w=500&h=500&fit=crop&q=80', // Tea
    'https://images.unsplash.com/photo-1581098365948-6b5c4d863572?w=500&h=500&fit=crop&q=80', // Soft drinks
    'https://images.unsplash.com/photo-1437418747212-8d9709afab22?w=500&h=500&fit=crop&q=80', // Juice glass
    'https://images.unsplash.com/photo-1546173159-315724a31696?w=500&h=500&fit=crop&q=80', // Cola
    'https://images.unsplash.com/photo-1556881286-fc6915169721?w=500&h=500&fit=crop&q=80', // Energy drink
  ],
  'snacks': [
    'https://images.unsplash.com/photo-1566478989037-eec170784d0b?w=500&h=500&fit=crop&q=80', // Chips
    'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=500&h=500&fit=crop&q=80', // Cookies
    'https://images.unsplash.com/photo-1511381939415-e44015466834?w=500&h=500&fit=crop&q=80', // Chocolate
    'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=500&h=500&fit=crop&q=80', // Nuts
    'https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=500&h=500&fit=crop&q=80', // Candy
    'https://images.unsplash.com/photo-1621939514649-280e2ee25f60?w=500&h=500&fit=crop&q=80', // Granola bars
    'https://images.unsplash.com/photo-1578849278619-e73505e9610f?w=500&h=500&fit=crop&q=80', // Popcorn
    'https://images.unsplash.com/photo-1599490659213-e2b9527bd087?w=500&h=500&fit=crop&q=80', // Biscuits
  ],
  'household': [
    'https://images.unsplash.com/photo-1563453392212-326f5e854473?w=500&h=500&fit=crop&q=80', // Cleaning supplies
    'https://images.unsplash.com/photo-1585421514738-01798e348b17?w=500&h=500&fit=crop&q=80', // Dish soap
    'https://images.unsplash.com/photo-1610557892470-55d9e80c0bce?w=500&h=500&fit=crop&q=80', // Laundry detergent
    'https://images.unsplash.com/photo-1631889993959-41b4e9c6e3c5?w=500&h=500&fit=crop&q=80', // Paper towels
    'https://images.unsplash.com/photo-1564053489984-317bbd824340?w=500&h=500&fit=crop&q=80', // Trash bags
    'https://images.unsplash.com/photo-1608485626704-f457c77ea5c0?w=500&h=500&fit=crop&q=80', // Kitchen supplies
  ],
  'personal-care': [
    'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=500&h=500&fit=crop&q=80', // Soap
    'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=500&h=500&fit=crop&q=80', // Shampoo
    'https://images.unsplash.com/photo-1589933767411-eda563447357?w=500&h=500&fit=crop&q=80', // Toothpaste
    'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=500&h=500&fit=crop&q=80', // Skincare
    'https://images.unsplash.com/photo-1616683693504-3ea7e9ad6fec?w=500&h=500&fit=crop&q=80', // Body wash
    'https://images.unsplash.com/photo-1556228578-8c89e6adf883?w=500&h=500&fit=crop&q=80', // Deodorant
    'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=500&h=500&fit=crop&q=80', // Hand soap
  ]
}

export function createProduct(): Product {
  const category = (faker.helpers as any).arrayElement(CATEGORIES)
  const categoryData = GROCERY_PRODUCTS[category.id] || GROCERY_PRODUCTS['pantry-staples']

  const productName = (faker.helpers as any).arrayElement(categoryData.products)
  const weight = (faker.helpers as any).arrayElement(categoryData.weights)
  const title = `${productName} - ${weight}`

  const originalPrice = faker.number.int({ min: 50, max: 5000 })
  const discountPercent = (faker.helpers as any).arrayElement(DISCOUNT_OPTIONS)
  const currentPrice = Math.round(originalPrice * (1 - discountPercent / 100))

  const productImages = PRODUCT_IMAGES[category.id] || PRODUCT_IMAGES['pantry-staples']
  const imageCount = faker.number.int({ min: 2, max: 4 })
  
  const images = Array.from({ length: imageCount }, (_, index) => {
    // Use different images from the category pool
    const imageUrl = productImages[index % productImages.length]
    return {
      url: imageUrl,
      alt: productName,
      is_primary: index === 0,
    }
  })

  const stockQuantity = faker.number.int({ min: 0, max: 500 })
  let stockStatus: Product['stock_status'] = 'in_stock'
  if (stockQuantity === 0) stockStatus = 'out_of_stock'
  else if (stockQuantity < 10) stockStatus = 'low_stock'

  const badges = (faker.helpers as any).arrayElements(
    BADGE_OPTIONS,
    faker.number.int({ min: 0, max: 2 })
  ) as Badge[]

  const freeShipping = (faker.helpers as any).maybe(() => true, { probability: 0.4 }) ?? false

  // Generate grocery-appropriate descriptions
  const descriptions = [
    `Fresh and high-quality ${productName.toLowerCase()} sourced from trusted suppliers. Perfect for your daily cooking needs.`,
    `Premium quality ${productName.toLowerCase()} at the best price. Carefully selected for freshness and taste.`,
    `Stock up on ${productName.toLowerCase()} for your kitchen. Great value for everyday essentials.`,
    `${productName} - A household favorite. Fresh, reliable, and delivered to your doorstep.`,
    `Get the best ${productName.toLowerCase()} delivered fresh. Ideal for your family's daily needs.`
  ]

  const keyFeatures = [
    'Fresh & High Quality',
    'Carefully Selected',
    'Best Value',
    'Hygienic Packaging',
    `Weight: ${weight}`,
    'Daily Essentials',
    'Family Pack Available',
    'Premium Quality'
  ]

  return {
    id: faker.string.uuid(),
    title,
    slug: faker.helpers.slugify(title).toLowerCase(),
    description: (faker.helpers as any).arrayElement(descriptions),
    images,
    vendor: createVendor(),
    pricing: {
      current_price: currentPrice,
      original_price: originalPrice,
      currency: 'BDT',
      currency_symbol: '৳',
      discount_percentage: discountPercent,
    },
    rating: {
      average: faker.number.float({ min: 3.0, max: 5.0, fractionDigits: 1 }),
      count: faker.number.int({ min: 5, max: 500 }),
    },
    shipping: {
      free_shipping: freeShipping,
      estimated_days: faker.number.int({ min: 1, max: 3 }),
      cost: freeShipping ? 0 : faker.number.int({ min: 30, max: 80 }),
    },
    stock_status: stockStatus,
    stock_quantity: stockQuantity,
    badges,
    category_id: category.id,
    category_name: category.name,
    created_at: faker.date.recent({ days: 30 }).toISOString(),
    key_features: (faker.helpers as any).arrayElements(keyFeatures, faker.number.int({ min: 3, max: 5 })),
  }
}
